import {
  mergeDataIntoQueryString,
  type Method,
  router,
  shouldIntercept,
  type LinkComponentBaseProps,
} from "@inertiajs/core";
import {
  type ComponentProps,
  createComponent,
  createMemo,
  type JSX,
  mergeProps,
  type ParentProps,
  splitProps,
} from "solid-js";
import { Dynamic, isServer } from "solid-js/web";

type ElementType = keyof JSX.IntrinsicElements;

interface BaseInertiaLinkProps<T extends ElementType = "a"> extends LinkComponentBaseProps {
  as?: T;
  onClick?: JSX.EventHandler<HTMLElement, MouseEvent>;
}

export type InertiaLinkProps<T extends ElementType = "a"> = BaseInertiaLinkProps<T> &
  Omit<ComponentProps<T>, keyof BaseInertiaLinkProps<T>>;

const noop = () => {};

export default function Link<T extends ElementType = "a">(
  _props: ParentProps<InertiaLinkProps<T>>,
) {
  let [props, attributes] = splitProps(_props, [
    "children",
    "as",
    "data",
    "href",
    "method",
    "preserveScroll",
    "preserveState",
    "replace",
    "only",
    "headers",
    "queryStringArrayFormat",
    "onClick",
    "onCancelToken",
    "onBefore",
    "onStart",
    "onProgress",
    "onFinish",
    "onCancel",
    "onSuccess",
    "onError",
  ]);

  // Set default prop values
  /* @ts-ignore */
  props = mergeProps(
    {
      as: "a",
      data: {},
      method: "get",
      preserveScroll: false,
      preserveState: null,
      replace: false,
      only: [],
      headers: {},
      queryStringArrayFormat: "brackets",
    },
    props,
  );

  // Mutate (once) props into prover values
  /* @ts-ignore */
  props = mergeProps(props, {
    /* @ts-ignore */
    as: props.as.toLowerCase() as InertiaLinkProps["as"],
    /* @ts-ignore */
    method: props.method.toLowerCase() as Method,
  });

  const mergedHrefAndData = createMemo(() => {
    const [href, data] = mergeDataIntoQueryString(
      /* @ts-ignore */
      props.method,
      props.href || "",
      props.data,
      props.queryStringArrayFormat,
    );
    return { href, data };
  });

  if (props.as === "a") {
    /* @ts-ignore */
    attributes = mergeProps(attributes, {
      get href() {
        return mergedHrefAndData().href;
      },
    });

    if (props.method !== "get") {
      console.warn(
        `Creating POST/PUT/PATCH/DELETE <a> links is discouraged as it causes "Open Link in New Tab/Window" accessibility issues.\n\nPlease specify a more appropriate element using the "as" attribute. For example:\n\n<Link href="${props.href}" method="${props.method}" as="button">...</Link>`,
      );
    }
  }

  const visit = (event: MouseEvent & { currentTarget: HTMLElement; target: Element }) => {
    if (isServer) return;

    props.onClick?.(event);

    // @ts-ignore
    if (shouldIntercept(event)) {
      event.preventDefault();

      const { href, data } = mergedHrefAndData();
      router.visit(href, {
        data,
        method: props.method,
        preserveScroll: props.preserveScroll,
        preserveState: props.preserveState ?? props.method === "get",
        replace: props.replace,
        only: props.only,
        headers: props.headers,
        /* @ts-ignore */
        onCancelToken: props.onCancelToken || noop,
        onBefore: props.onBefore || noop,
        onStart: props.onStart || noop,
        onProgress: props.onProgress || noop,
        onFinish: props.onFinish || noop,
        onCancel: props.onCancel || noop,
        onSuccess: props.onSuccess || noop,
        onError: props.onError || noop,
      });
    }
  };

  return createComponent(
    // @ts-ignore
    Dynamic,
    mergeProps(attributes, {
      get component() {
        return props.as;
      },
      get children() {
        return props.children;
      },
      onClick: visit,
    }),
  );
}
